import React, { useMemo, useState } from 'react'
import { Link, router, useForm } from '@inertiajs/react'
import UserLayout from '../../../Layouts/UserLayout'

function parseKeywords(input) {
  return input
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
}

export default function Edit({ blog_post, errors: serverErrors }) {
  const { data, setData, put, processing, errors } = useForm({
    primary_query: blog_post.primary_query || '',
    secondary_keywords: blog_post.secondary_keywords || [],
    title: blog_post.title || '',
    meta_title: blog_post.meta_title || '',
    meta_description: blog_post.meta_description || '',
    og_title: blog_post.og_title || '',
    og_description: blog_post.og_description || '',
    slug: blog_post.slug || '',
    outline: blog_post.outline || '',
    body_html: blog_post.body_html || '',
    internal_links: blog_post.internal_links || [],
    thumbnail_url: blog_post.thumbnail_url || ''
  })

  const allErrors = serverErrors || errors

  const internalLinksPreview = useMemo(() => {
    return Array.isArray(data.internal_links) ? data.internal_links : []
  }, [data.internal_links])

  function submit(e) {
    e.preventDefault()
    put(`/admin/blog_posts/${blog_post.slug}`)
  }

  const [translateLocales, setTranslateLocales] = useState([])
  const [isTranslating, setIsTranslating] = useState(false)
  const [translateProgress, setTranslateProgress] = useState(0)
  const [translateResult, setTranslateResult] = useState(null)

  function publish() {
    if (isTranslating) return
    router.patch(`/admin/blog_posts/${blog_post.slug}/publish`)
  }

  function unpublish() {
    if (isTranslating) return
    router.patch(`/admin/blog_posts/${blog_post.slug}/unpublish`)
  }

  const LOCALES = [
    { code: 'fr', name: 'French' },
    { code: 'es', name: 'Spanish' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'ja', name: 'Japanese' },
    { code: 'zh', name: 'Chinese' },
  ]

  async function translateSelected() {
    if (translateLocales.length === 0) {
      alert('Pick at least one language to translate to.')
      return
    }

    if (isTranslating) return

    setIsTranslating(true)
    setTranslateProgress(5)
    setTranslateResult(null)

    // Fake progress bar while server runs translations (no streaming yet)
    let timer = null
    try {
      timer = window.setInterval(() => {
        setTranslateProgress(p => {
          const next = p + Math.max(1, Math.round((100 - p) * 0.08))
          return next >= 90 ? 90 : next
        })
      }, 600)

    try {
      const response = await fetch(`/admin/blog_posts/${blog_post.slug}/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ locales: translateLocales })
      })
        const contentType = response.headers.get('content-type') || ''
        const isJson = contentType.includes('application/json')

        if (!response.ok) {
          // Gateways / proxies often return an HTML error page for 502/504.
          const bodyText = await response.text()
          throw new Error(`Translate failed (${response.status}). ${isJson ? '' : bodyText.slice(0, 200)}`)
        }

        if (!isJson) {
          const bodyText = await response.text()
          throw new Error(`Translate failed: expected JSON, got "${contentType}". ${bodyText.slice(0, 200)}`)
        }

        const data = await response.json()

        setTranslateResult(data)
        setTranslateProgress(100)

        const translated = (data.translated_locales || []).join(', ')
        const failed = data.failed_locales || {}
        const failedKeys = Object.keys(failed)

        if (failedKeys.length > 0) {
          alert(`Translated: ${translated || 'none'}\nFailed: ${failedKeys.join(', ')}`)
        } else {
          alert(`Translated: ${translated || 'none'}`)
        }
      } catch (e) {
        console.error(e)
        alert(`Translation failed. ${e?.message || 'Check console.'}`)
      }
    } finally {
      if (timer) window.clearInterval(timer)
      setTimeout(() => {
        setIsTranslating(false)
        setTranslateProgress(0)
      }, 400)
    }
  }

  return (
    <UserLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-start mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold">Edit Blog Post</h1>
            <div className="mt-2 flex items-center gap-3">
              {blog_post.published ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                  Published
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                  Draft
                </span>
              )}
              <span className="text-sm text-gray-500 font-mono">/blog/{blog_post.slug}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-end">
            <a
              href={`/admin/blog_posts/${blog_post.id}/preview`}
              target="_blank"
              rel="noreferrer"
              className="px-3 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
            >
              Preview
            </a>
            {blog_post.published && (
              <a
                href={`/blog/${blog_post.slug}`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-2 rounded border border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 text-sm font-medium"
              >
                Public
              </a>
            )}
            {blog_post.published ? (
              <button
                type="button"
                onClick={unpublish}
                className="px-3 py-2 rounded bg-gray-800 text-white hover:bg-gray-900 text-sm font-medium"
              >
                Unpublish
              </button>
            ) : (
              <button
                type="button"
                onClick={publish}
                className="px-3 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium"
              >
                Publish
              </button>
            )}
            <Link href="/admin/blog_posts" className="px-3 py-2 text-gray-600 hover:text-gray-900 text-sm font-medium">
              Back
            </Link>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 space-y-6">
          <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4">
            {isTranslating && (
              <div className="mb-3">
                <div className="h-2 w-full bg-indigo-100 rounded-full overflow-hidden">
                  <div
                    className="h-2 bg-indigo-600 rounded-full transition-all"
                    style={{ width: `${translateProgress}%` }}
                  />
                </div>
                <div className="mt-2 text-xs text-indigo-900">
                  Translating… please wait (don’t publish/save during this).
                </div>
              </div>
            )}
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-semibold text-indigo-900">Translations</div>
                <div className="text-sm text-indigo-800 mt-1">
                  Choose languages to translate this post. Visitors will see the translated version when their app language matches; otherwise it falls back to English.
                </div>
              </div>
              <button
                type="button"
                onClick={translateSelected}
                disabled={isTranslating}
                className="shrink-0 px-3 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {isTranslating ? 'Translating…' : 'Translate'}
              </button>
            </div>

            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
              {LOCALES.map(l => (
                <label key={l.code} className="flex items-center gap-2 text-sm text-indigo-900">
                  <input
                    type="checkbox"
                    checked={translateLocales.includes(l.code)}
                    disabled={isTranslating}
                    onChange={(e) => {
                      setTranslateLocales(prev => (
                        e.target.checked
                          ? [...prev, l.code]
                          : prev.filter(x => x !== l.code)
                      ))
                    }}
                  />
                  <span>{l.name}</span>
                </label>
              ))}
            </div>

            {translateResult?.failed_locales && Object.keys(translateResult.failed_locales).length > 0 && (
              <div className="mt-3 text-sm text-red-700">
                Some translations failed:
                <ul className="list-disc list-inside mt-1">
                  {Object.entries(translateResult.failed_locales).map(([loc, msg]) => (
                    <li key={loc}><span className="font-mono">{loc}</span>: {msg}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Primary query</label>
            <input
              type="text"
              value={data.primary_query}
              onChange={e => setData('primary_query', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
            />
            {allErrors?.primary_query && <div className="text-red-500 text-sm mt-1">{allErrors.primary_query}</div>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Secondary keywords</label>
            <input
              type="text"
              value={(Array.isArray(data.secondary_keywords) ? data.secondary_keywords : []).join(', ')}
              onChange={e => setData('secondary_keywords', parseKeywords(e.target.value))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">Banner image</label>
            {data.thumbnail_url ? (
              <div className="space-y-3">
                <img src={data.thumbnail_url} alt="Banner" className="h-44 w-full object-cover rounded-md" />
                <div>
                  <button type="button" onClick={() => setData('thumbnail_url', '')} className="text-sm text-red-600 hover:text-red-800 underline">
                    Remove image
                  </button>
                </div>
              </div>
            ) : (
              <input
                type="text"
                placeholder="Paste image URL (optional)"
                value={data.thumbnail_url}
                onChange={e => setData('thumbnail_url', e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm border p-2 text-sm"
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">H1 / Title</label>
            <input
              type="text"
              value={data.title}
              onChange={e => setData('title', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
            />
            {allErrors?.title && <div className="text-red-500 text-sm mt-1">{allErrors.title}</div>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Meta title (≤ 60 chars)</label>
              <input
                type="text"
                value={data.meta_title}
                onChange={e => setData('meta_title', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Slug</label>
              <input
                type="text"
                value={data.slug}
                onChange={e => setData('slug', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2 font-mono"
              />
              {allErrors?.slug && <div className="text-red-500 text-sm mt-1">{allErrors.slug}</div>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Meta description (155–160 chars)</label>
            <textarea
              rows="2"
              value={data.meta_description}
              onChange={e => setData('meta_description', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">OG title</label>
              <input
                type="text"
                value={data.og_title}
                onChange={e => setData('og_title', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">OG description</label>
              <input
                type="text"
                value={data.og_description}
                onChange={e => setData('og_description', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Outline</label>
            <textarea
              rows="6"
              value={data.outline}
              onChange={e => setData('outline', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2 font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Body HTML</label>
            <textarea
              rows="16"
              value={data.body_html}
              onChange={e => setData('body_html', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2 font-mono text-sm"
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800">Internal link suggestions</h3>
              <span className="text-xs text-gray-500">{internalLinksPreview.length} items</span>
            </div>
            {internalLinksPreview.length > 0 ? (
              <ul className="mt-3 space-y-2 text-sm">
                {internalLinksPreview.map((l, i) => (
                  <li key={i} className="bg-white border rounded p-3">
                    <div className="font-medium text-gray-900">{l.anchor_text}</div>
                    <div className="text-gray-600 font-mono">{l.url}</div>
                    {l.context && <div className="text-gray-500 mt-1">{l.context}</div>}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-2 text-sm text-gray-500 italic">No suggestions.</div>
            )}
          </div>

          <div className="flex justify-end gap-4 pt-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              {processing ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </UserLayout>
  )
}


